import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Directory from "@/pages/Directory";
import PsychologistProfile from "@/pages/PsychologistProfile";
import Initiatives from "@/pages/Initiatives";
import InitiativeDetail from "@/pages/InitiativeDetail";
import Opportunities from "@/pages/Opportunities";
import JoinPsychologist from "@/pages/JoinPsychologist";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PsychologistWorkspace from "@/pages/PsychologistWorkspace";
import OrganizerDashboard from "@/pages/OrganizerDashboard";
import OrganizerInitiativeWorkspace from "@/pages/OrganizerInitiativeWorkspace";
import AdminArea from "@/pages/AdminArea";
import ClientAppointments from "@/pages/ClientAppointments";
import ClientSettings from "@/pages/ClientSettings";
import NotFound from "@/pages/NotFound";
import { Privacy, Terms, Disclaimer, Copyright } from "@/pages/LegalPages";

// Backwards-compatible redirects: legacy /psicologos URLs -> canonical /profesionales.
function LegacyProfileRedirect() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/profesionales/${id}${search}`} replace />;
}
function LegacyDirectoryRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/profesionales${search}`} replace />;
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/encontrar-apoyo" element={<Directory variant="find" />} />
                <Route path="/profesionales" element={<Directory variant="directory" />} />
                <Route path="/profesionales/:id" element={<PsychologistProfile />} />
                <Route path="/psicologos" element={<LegacyDirectoryRedirect />} />
                <Route path="/psicologos/:id" element={<LegacyProfileRedirect />} />
                <Route path="/iniciativas" element={<Initiatives />} />
                <Route path="/iniciativas/:id" element={<InitiativeDetail />} />
                <Route path="/oportunidades" element={<Opportunities />} />
                <Route path="/oportunidades/:id" element={<InitiativeDetail />} />
                <Route path="/unirme" element={<JoinPsychologist />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/recuperar" element={<ForgotPassword />} />
                <Route path="/restablecer" element={<ResetPassword />} />
                <Route path="/legal/privacidad" element={<Privacy />} />
                <Route path="/legal/terminos" element={<Terms />} />
                <Route path="/legal/aviso" element={<Disclaimer />} />
                <Route path="/legal/derechos" element={<Copyright />} />
                <Route path="/mi-espacio" element={<ProtectedRoute roles={["psychologist"]}><PsychologistWorkspace /></ProtectedRoute>} />
                <Route path="/mis-citas" element={<ProtectedRoute roles={["client", "psychologist"]}><ClientAppointments /></ProtectedRoute>} />
                <Route path="/mi-cuenta" element={<ProtectedRoute roles={["client"]}><ClientSettings /></ProtectedRoute>} />
                <Route path="/organizador" element={<ProtectedRoute roles={["organizer"]}><OrganizerDashboard /></ProtectedRoute>} />
                <Route path="/organizador/iniciativa/:id" element={<ProtectedRoute roles={["organizer"]}><OrganizerInitiativeWorkspace /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminArea /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
